import React, { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";
import { isNil } from "lodash";
import { Loader2 } from "lucide-react";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { useJustCreated } from "@/components/common/enrichment-async-hint";
import { CountrySelect } from "@/components/fields/country-select";
import { RemoteSelect } from "@/components/fields/remote-select";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getChangedValues } from "@/lib/utils";
import { parseRead } from "@/lib/zod/read-schema";
import MoeMappingField from "@/pages/geodata/components/MoeMappingField";
import { GeodataPreview } from "@/pages/geodata/components/geodata-preview";
import { useGeodataRepository } from "@/pages/geodata/hooks/use-geodata-repository";
import {
  GeodataEditFormType,
  GeodataGeoDbResultType,
  GeodataViewType,
  geodataEditSchema,
} from "@/pages/geodata/schemas/geodata.edit.form";

const geodataEditSchemaProvider = new ZodProvider(geodataEditSchema);

export const GeodataEdit = () => {
  const {
    get,
    getUsageCount,
    update,
    searchGeoDB,
    getTimezone,
    create,
    triggerEnrich,
  } = useGeodataRepository();
  const { id } = useParsed();
  const [values, setValues] = React.useState<GeodataViewType>();

  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();

  const setTitle = useDocumentTitle();

  const [usedOn, setUsedOn] = React.useState<number>(0);
  const [usedOnVenues, setUsedOnVenues] = React.useState<number>(0);
  const [geoDbCountry, setGeoDbCountry] = React.useState<string | undefined>();
  const justCreated = useJustCreated();

  useEffect(() => {
    if (!id) {
      setWarnWhen(true);
      return;
    }
    get(id!.toString())
      .then((response) => {
        setValues(response);
        setTitle("Edit " + response.name);
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get geodata",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!isNil(id) && values?.name) {
      getUsageCount(values.name.toString()).then((result) => {
        setUsedOn(result.events);
        setUsedOnVenues(result.venues);
      });
    }
  }, [id, values?.name]);

  const initializeForm = (
    form: UseFormReturn<GeodataEditFormType, any, GeodataEditFormType>,
  ) => {
    form.watch((changedValues) => {
      if (!values) return;
      const changed = getChangedValues(
        changedValues,
        values as GeodataEditFormType,
      );
      console.debug("Form Changed", changed);
      setWarnWhen(Object.keys(changed).length > 0);
    });
  };

  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async (values: GeodataEditFormType) => {
    setSubmitting(true);
    try {
      if (!id) {
        await create(values);
        return;
      }
      await update(values);
      setWarnWhen(false);
    } catch (error) {
      // notifications are handled by the repository's onError callbacks
      console.error("Geodata submit failed", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-row">
      <div className="w-3/4 p-8 flex flex-col space-y-4">
        <div className="grid grid-cols-2 gap-x-2">
          <RemoteSelect<GeodataGeoDbResultType>
            triggerType="change"
            paginated
            onValueChanged={async (value) => {
              const next = value
                ? { ...value, region: value.region ?? "" }
                : undefined;
              setValues(next);
              if (next && next.geoDbId) {
                const timezone = await getTimezone(next.geoDbId.toString());
                setValues({
                  ...next,
                  timezone: timezone,
                });
                return;
              }
            }}
            accessorFn={(option: any) => ({
              id: option.id?.toString() ?? "",
              name: `${option.name} (${option.countryCode}) - ${option.type}`,
            })}
            onSearch={async (query, onStatusChanged, offset) => {
              onStatusChanged("loading");
              const data = await searchGeoDB(query, geoDbCountry, offset);
              onStatusChanged("success");
              return data;
            }}
            label="Fetch from GeoDB"
          />
          <CountrySelect
            label="Filter by country"
            value={geoDbCountry}
            onValueChanged={setGeoDbCountry}
          />
        </div>
        {values && (
          <AutoForm
            key={JSON.stringify(values)}
            schema={geodataEditSchemaProvider}
            defaultValues={parseRead(geodataEditSchema, values, "geodata form")}
            onFormInit={initializeForm}
            onSubmit={onSubmit}
            {...injectCustomAutoFormComponents({
              formComponents: { moeMapping: MoeMappingField },
              uiComponents: {},
            })}
          >
            <BottomActionBar>
              <Button disabled={!warnWhen || submitting} type="submit">
                {submitting && (
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                )}
                Submit
              </Button>
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
      {values && (
        <GeodataPreview
          item={values!}
          usedOn={usedOn}
          usedOnVenues={usedOnVenues}
          justCreated={justCreated}
          onTriggerEnrich={
            id
              ? async () => {
                  const success = await triggerEnrich(id.toString());
                  if (success) {
                    const refreshed = await get(id.toString()).catch(
                      () => null,
                    );
                    if (refreshed) setValues(refreshed);
                  }
                  return success;
                }
              : undefined
          }
        />
      )}
    </div>
  );
};
